import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import CustomizeIcon from '@mui/icons-material/Build';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CloudIcon from '@mui/icons-material/Cloud';
import SupportIcon from '@mui/icons-material/Support';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { Link } from 'react-router-dom';
import Button from '../components/Button';
import SEO from '../components/SEO';

const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '40vh',
  minHeight: '300px',
  color: 'white',
  textAlign: 'center',
  overflow: 'hidden',
  background: 'linear-gradient(45deg, #3c005a 30%, #5c0080 90%)',
  padding: theme.spacing(8, 0),
}));

const BannerImage = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  transition: 'opacity 1s ease-in-out',
}));

const Overlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  height: '100%',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
  },
}));

const homeStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Thrana Pharmacovigilance Solutions',
  url: 'https://www.thranatech.com/',
  description:
    'Thrana delivers pharmacovigilance platforms, services, and training to help pharmaceutical organizations ensure global drug safety compliance.',
  publisher: {
    '@type': 'Organization',
    name: 'Thranatech.com',
    url: 'https://www.thranatech.com',
    logo: 'https://www.thranatech.com/logo192.png',
    sameAs: [
      'https://www.linkedin.com/company/thrana-softech-private-limited'
    ],
  },
  mainEntity: {
    '@type': 'Organization',
    name: 'Thrana Softech Private Limited',
    alternateName: ['Thranatech', 'Thranatech.com'],
    url: 'https://www.thranatech.com',
    logo: 'https://www.thranatech.com/logo192.png',
    description: 'Thrana Softech Private Limited provides pharmacovigilance software solutions, drug safety platforms, and professional training in Pharmacovigilance and Clinical Research.',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@thranatech.com',
      contactType: 'customer service',
    },
  },
};

const Home = () => {
  const theme = useTheme();
  const [currentBanner, setCurrentBanner] = useState(0);

  const banners = [
    '/banner1.jpg',
    '/auditandInspection1.jpg',
    '/banner2.jpg',
    '/productsSafety.jpg',
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 5000); // Change banner every 5 seconds

    return () => clearInterval(timer);
  }, []);

  const features = [
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Adverse Event Tracking',
      description: 'Tracks and manages adverse events efficiently',
    },
    {
      icon: <AssessmentIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Regulatory Compliance',
      description: 'Meets global regulatory standards',
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Real-time Data Access',
      description: 'Real-time access for prompt decisions',
    },
    {
      icon: <CustomizeIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Customizable Software',
      description: 'Accommodates organizational needs',
    },
    {
      icon: <CloudIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Cloud-Based Solution',
      description: 'Access your data securely from anywhere',
    },
    {
      icon: <SupportIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: '24/7 Support',
      description: 'Round-the-clock technical assistance',
    },
  ];

  return (
    <>
      <SEO
        title="Thrana | Pharmacovigilance Software, Services & Training"
        description="Thrana delivers pharmacovigilance software, safety services, and pharma training to help life sciences organizations stay compliant and protect patient safety."
        path="/"
        structuredData={homeStructuredData}
      />
      <Box>
      <HeroSection>
        {banners.map((banner, index) => (
          <BannerImage
            key={index}
            sx={{
              backgroundImage: `url(${banner})`,
              opacity: currentBanner === index ? 1 : 0,
            }}
          />
        ))}
        <Overlay>
          <Container maxWidth="md">
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                fontWeight: 'bold',
                color: 'white',
              }}
            >
              Thrana Pharmacovigilance Safety Platform
            </Typography>
            <Typography
              variant="h5"
              paragraph
              sx={{
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                mb: 4,
                color: 'white',
              }}
            >
              Your Safety Shield, Safeguarding Health with Precision
            </Typography>
            <Button
              component={Link}
              to="/contact"
              size="large"
            >
              Get Started
            </Button>
          </Container>
        </Overlay>
      </HeroSection>

      <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom>
            About Thrana
          </Typography>
          <Typography variant="body1" paragraph sx={{ textAlign: 'justify', fontSize: '1.1rem' }}>
            Thrana Softtech Pvt. Ltd., headquartered in Bengaluru, is a leading software development
            company that specializes in advanced pharmacovigilance and drug safety solutions for the Life
            Sciences sector.
          </Typography>
          <Typography variant="body1" paragraph sx={{ textAlign: 'justify', fontSize: '1.1rem' }}>
            Pharmacovigilance is a critical function in the life cycle of the drug focusing on patient safety
            and quality of life. While the principle of drug safety remains same globally, the requirement of
            every Regulatory Authority is very specific and yet to be completely harmonized. Additionally,
            pharmaceutical organizations are challenged by the ever-changing regulatory requirements and unique
            needs of commercial partners.
          </Typography>
          <Typography variant="body1" paragraph sx={{ textAlign: 'justify', fontSize: '1.1rem' }}>
            At Thrana, we focus on enabling our customers to deliver quality pharmaceuticals and medical devices
            with our expertise in risk minimization and maximizing patient safety. Our alignment to Good
            Pharmacovigilance Practice benefits our customers in establishing confidence in Health Care Professions
            and Patients across global markets. Our comprehensive pharmacovigilance services are focused towards
            achieving customer satisfaction while prioritizing patient safety throughout drug development and
            commercialization. Company can customize solutions to suite specific drug safety requirements and rapidly
            scale up for complete pharmacovigilance delivery.
          </Typography>
          <Typography variant="body1" sx={{ textAlign: 'justify', fontSize: '1.1rem' }}>
            At Thrana, we can extend our support to host your safety data in compliance to various privacy laws and
            successful regulate the exchange of your pharmacovigilance data. Our expert team provides a range of distinct
            solutions in maintenance of system master file, product dictionary and regulatory downloads that cater to
            specific requirements. Our multi-disciplinary team integrates expertise in various domains to support our
            customer in audits, inspections, literatures and translations service. Our continuous learning process and up
            to the minute regulatory insight facilitates constant monitoring of Key Performance Indicators and enable our
            team of experts to achieve high standards of quality and regulatory compliance.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" textAlign="center" gutterBottom>
          Why Choose Us
        </Typography>
        <Typography variant="h6" textAlign="center" color="text.secondary" paragraph>
          Thrana software solutions are tailored to elevate your drug safety standards.
        </Typography>

        <Grid container spacing={4} sx={{ mt: 4 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <FeatureCard elevation={3}>
                <CardContent sx={{ textAlign: 'center' }}>
                  {feature.icon}
                  <Typography variant="h5" component="h3" sx={{ mt: 2, mb: 1 }}>
                    {feature.title}
                  </Typography>
                  <Typography color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </FeatureCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Hidden AI solutions section (kept for future use) */}
      {false && (
        <Container sx={{ py: 8 }}>
          <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ color: '#3c005a' }}>
            Why Choose Our AI Solutions?
          </Typography>
          <Grid container spacing={4} sx={{ mt: 4 }}>
            <Grid item xs={12} md={3}>
              <FeatureCard elevation={3}>
                <AutoGraphIcon sx={{ fontSize: 60, color: '#3c005a', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ color: '#3c005a' }}>
                  Advanced AI Analytics
                </Typography>
                <Typography>
                  Leverage cutting-edge machine learning algorithms for real-time adverse event detection and analysis
                </Typography>
              </FeatureCard>
            </Grid>
            <Grid item xs={12} md={3}>
              <FeatureCard elevation={3}>
                <SecurityIcon sx={{ fontSize: 60, color: '#3c005a', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ color: '#3c005a' }}>
                  Regulatory Compliance
                </Typography>
                <Typography>
                  Automated compliance monitoring and reporting aligned with global pharmacovigilance standards
                </Typography>
              </FeatureCard>
            </Grid>
            <Grid item xs={12} md={3}>
              <FeatureCard elevation={3}>
                <SpeedIcon sx={{ fontSize: 60, color: '#3c005a', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ color: '#3c005a' }}>
                  Real-time Monitoring
                </Typography>
                <Typography>
                  Instant detection and analysis of potential safety signals across multiple data sources
                </Typography>
              </FeatureCard>
            </Grid>
            <Grid item xs={12} md={3}>
              <FeatureCard elevation={3}>
                <AnalyticsIcon sx={{ fontSize: 60, color: '#3c005a', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ color: '#3c005a' }}>
                  Predictive Insights
                </Typography>
                <Typography>
                  Proactive risk assessment and prediction using advanced AI models and data analytics
                </Typography>
              </FeatureCard>
            </Grid>
          </Grid>
        </Container>
      )}

      <Box sx={{ bgcolor: '#f5f5f5', py: 8 }}>
        <Container>
          <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ color: '#3c005a' }}>
            Transform Your Pharmacovigilance Operations
          </Typography>
          <Typography variant="body1" align="center" paragraph>
            Our Safety Database platform helps pharmaceutical companies streamline their safety monitoring processes,
            reduce manual workload, and make data-driven decisions faster than ever before.
          </Typography>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              component={Link}
              to="/services"
              size="large"
            >
              Explore Our Solutions
            </Button>
          </Box>
        </Container>
      </Box>
      </Box>
    </>
  );
};

export default Home; 